/**
 *
 *  Time Slots pop-up
 *  Component
 *
 *  Created by Vladimir Shishlyannikov on 13/03/2023.
 *  Copyright (c) 2023-present iWoo Ltd. All rights reserved.
 *
 */

import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types"; // use directly
import { Link } from "react-router-dom";

// Components
import Button from "../Button/Button";
import BookingFlowDayTimeSelector from "../BookingFlow/DayTimeSelector/DayTimeSelector";
import BookingFlowWorkoutTypeSelector from "../BookingFlow/WorkoutTypeSelector/WorkoutTypeSelector";

// Icons
import CloseIcon from "../../icons/CloseIcon";

// Utils
import { getMoment } from "../../utils/date";
import { getPartnerPictureUrl } from "../../utils";

import defaultPartnerPhoto from "../../images/generic-trainerphoto.svg";
import ArrowRightIcon from "../../icons/ArrowRightIcon";

const BookingFlowPopup = React.forwardRef(
  (
    {
      className,
      disabled,
      isFetching,
      // culture,
      region,
      partnerId,
      bookingStep,
      bookingTime,
      constrainTimesWithinRange,
      min,
      max,
      availableDays,
      workoutValues,
      workout,
      // sessionType,
      partnerSummary,
      loadPartnerProfile,
      partnerLoaded,
      partnerAvailability,
      partnerAvailabilityLoaded,
      // clearPartnerSummaryReceive,
      clearPartnerAvailabilityLoaded,
      onDateTimeSelect,
      onWorkoutChange,
      onCtaClick,
      onClose,
    },
    outerRef
  ) => {
    const dayTimePickerRef = useRef();

    const partnerFirstName = partnerSummary && partnerSummary.firstName;
    const partnerFamilyName = partnerSummary && partnerSummary.familyName;

    useEffect(() => {
      if (partnerId) {
        loadPartnerProfile(partnerId);
      }

      return () => {
        clearPartnerAvailabilityLoaded();
        // clearPartnerSummaryReceive();
      };
    }, [partnerId]); // eslint-disable-line

    /*
     * @param {Date} dateTime
     * @param {String} dateStr - formatted string
     * @param {boolean} time - is time selected
     *
     */
    const handleDateTimeSelect = (dateTime, dateStr, time) => {
      // Pass through callback to parent
      onDateTimeSelect && onDateTimeSelect(dateTime, time);
    };

    const trainerPictureUrl =
      partnerSummary && getPartnerPictureUrl(partnerSummary);

    let canBook =
      bookingStep === "CHOOSE_DAY_TIME" || // We always have date set
      (workout && bookingStep === "CHOOSE_WORKOUT_TYPE") ||
      workout;
    const isOnline = false;

    return (
      <aside
        ref={outerRef}
        className={`booking-flow-popup tbk-fixed tbk-top-0 tbk-right-0 tbk-bottom-0 tbk-z-995 tbk-flex tbk-w-94 tbk-flex-col tbk-bg-basic-white tbk-shadow-trube ${className}`}
      >
        <div className="booking-flow-popup-header tbk-flex tbk-border-b tbk-border-solid tbk-border-[#e3e7eb] tbk-p-2">
          <button
            className="tbk-absolute tbk-right-3 tbk-text-coral"
            onClick={onClose}
          >
            <CloseIcon />
          </button>
          <Link to={`/partners/${partnerSummary && partnerSummary.id}`}>
            <div className={`tb-profile-picture tbk-mr-1.5`}>
              <img
                className="tbk-top-0 tbk-h-6 tbk-w-6 tbk-rounded-lg tbk-object-cover"
                itemProp="image"
                src={partnerLoaded ? trainerPictureUrl : defaultPartnerPhoto}
                alt={`${partnerFirstName} ${partnerFamilyName} | Personal Trainer in Los Angeles`}
              />
            </div>
          </Link>
          <div>
            {partnerLoaded ? (
              <Link
                to={`/partners/${partnerSummary.id}`}
                className="tbk-text-h3-subtitle tbk-mb-1.5 tbk-inline-block tbk-text-grey-main"
              >
                {partnerSummary.firstName}
              </Link>
            ) : (
              <div className="tbk-mb-1.5 tbk-h-[30px] tbk-w-8 tbk-animate-flash tbk-rounded-lg tbk-bg-blue-grey-light" />
            )}
            <div className="tbk-text-small-bold tb-vertical-alignment-fix tbk-uppercase tbk-text-blue-grey">
              {isOnline ? "Online" : "In-Person"}
            </div>
          </div>
        </div>
        <div className="booking-flow-popup-body tbk-text-main tbk-grow tbk-overflow-auto">
          {bookingStep === "CHOOSE_DAY_TIME" ? (
            <BookingFlowDayTimeSelector
              id="booking_flow_popup"
              disabled={disabled || isFetching}
              isFullyOpen={true}
              region={region}
              constrainTimesWithinRange={constrainTimesWithinRange}
              className={`booking-datetime-picker-input_what ${
                isOnline ? "virtual" : "in-person"
              }`}
              availableDays={availableDays}
              view="week"
              min={min}
              max={max}
              minTime="5:30"
              format="DD MMM, hh:mm A"
              headerFormat="MMMM"
              footerFormat="DD MMM"
              timeFormat="HH:mm"
              partnerAvailability={partnerAvailability}
              partnerAvailabilityLoaded={partnerAvailabilityLoaded}
              step={30}
              bookingTime={bookingTime && getMoment(bookingTime).toDate()}
              timeComponent={({ item }) => (
                <button type="button" disabled={item.disabled}>
                  {item.label}
                </button>
              )}
              onDateTimeSelect={handleDateTimeSelect}
              ref={dayTimePickerRef}
            />
          ) : (
            <BookingFlowWorkoutTypeSelector
              workoutValues={workoutValues}
              workout={workout}
              onWorkoutChange={onWorkoutChange}
            />
          )}
        </div>
        <Button
          className="tbk-mx-2 tbk-mb-3"
          disabled={!canBook}
          onClick={onCtaClick}
          endIcon={<ArrowRightIcon />}
        >
          {bookingStep === "CHOOSE_DAY_TIME"
            ? "Next"
            : `Book ${partnerLoaded && partnerSummary.firstName}`}
        </Button>
      </aside>
    );
  }
);

BookingFlowPopup.propTypes = {
  sessionType: PropTypes.string,
  partnerId: PropTypes.number,
  bookingStep: PropTypes.string,
  bookingTime: PropTypes.instanceOf(Date),
  workout: PropTypes.object, //workout,
  workoutValues: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
    })
  ),
  partnerSummary: PropTypes.shape({
    firstName: PropTypes.string,
    id: PropTypes.number,
    lastName: PropTypes.string,
    phoneNumber: PropTypes.string,
    products: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
      })
    ),
    region: PropTypes.string,
  }),
  loadPartnerProfile: PropTypes.func.isRequired,
  partnerLoaded: PropTypes.bool,
  partnerAvailability: PropTypes.arrayOf(
    PropTypes.shape({
      start: PropTypes.string,
    })
  ),
  partnerAvailabilityLoaded: PropTypes.bool,
  // clearPartnerSummaryReceive,
  clearPartnerAvailabilityLoaded: PropTypes.func,
  onClose: PropTypes.func,
  onCtaClick: PropTypes.func,
  onDateTimeSelect: PropTypes.func.isRequired,
  onWorkoutChange: PropTypes.func.isRequired,
};

BookingFlowPopup.defaultProps = {
  bookingStep: 1,
};

export default BookingFlowPopup;
